import { tokenStore } from "./tokenStore";
import { getApiUrl } from "./api";
import { createApiError } from "./apiError";
import { withApiLanguageHeader } from "./languageHeaders";

export { getApiUrl };

let refreshInFlight = null;

const authDebugEnabled = () => {
  if (process.env.REACT_APP_AUTH_DEBUG === "true") return true;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("auth:debug") === "true";
  } catch {
    return false;
  }
};

const authDebug = (event, details = {}) => {
  if (authDebugEnabled()) {
    // Tokens are deliberately never included in diagnostics.
    console.info(`[auth] ${event}`, details);
  }
};

export class RefreshSessionError extends Error {
  constructor(message, { status = null, cause = null } = {}) {
    super(message);
    this.name = "RefreshSessionError";
    this.status = status;
    this.cause = cause;
    this.sessionRejected = status === 401 || status === 403;
  }
}

export async function refreshAccessToken() {
  if (!refreshInFlight) {
    const revision = tokenStore.getRevision();
    refreshInFlight = (async () => {
      authDebug("REFRESH_STARTED");
      try {
        authDebug("REFRESH_COOKIE_SENT", { credentials: "include" });
        const res = await fetch(getApiUrl("api/Auth/refresh"), {
          method: "POST",
          headers: withApiLanguageHeader(),
          credentials: "include",
        });

        if (!res.ok) {
          authDebug("REFRESH_FAILED", { status: res.status });
          throw new RefreshSessionError("Failed to refresh session", {
            status: res.status,
          });
        }

        const data = await safeParse(res);
        const token = data && typeof data === "object" ? data.token : null;
        if (!token) {
          authDebug("REFRESH_FAILED", { reason: "missing_access_token" });
          throw new RefreshSessionError("Refresh response did not contain an access token");
        }

        if (tokenStore.getRevision() !== revision) {
          const currentToken = tokenStore.get();
          if (currentToken) return currentToken;
          throw new RefreshSessionError("Session changed during refresh");
        }
        tokenStore.set(token);
        authDebug("REFRESH_SUCCESS");
        authDebug("ACCESS_TOKEN_UPDATED");
        return token;
      } catch (error) {
        if (tokenStore.getRevision() !== revision) {
          const currentToken = tokenStore.get();
          if (currentToken) return currentToken;
          throw new RefreshSessionError("Session changed during refresh");
        }
        if (error instanceof RefreshSessionError) throw error;
        authDebug("REFRESH_FAILED", { reason: "network_or_cors" });
        throw new RefreshSessionError("Refresh request failed", { cause: error });
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

export async function getAccessTokenForSignalR() {
  if (refreshInFlight) {
    const token = await refreshInFlight.catch(() => null);
    if (token) return token;
  }
  return tokenStore.get() || "";
}

export async function apiFetch(input, opts = {}) {
  const url = /^https?:\/\//i.test(input)
    ? input
    : getApiUrl(input.replace(/^\/+/, ""));

  const requestRevision = tokenStore.getRevision();
  const requestToken = tokenStore.get();
  if (isJwtExpired(requestToken)) authDebug("ACCESS_TOKEN_EXPIRED");
  let res = await doFetch(url, opts, requestToken);

  if (res.status !== 401) {
    if (!res.ok) throw await toError(res);
    return parseByOptions(res, opts);
  }

  authDebug("API_401", {
    path: safePath(url),
    hadAccessToken: Boolean(requestToken),
  });

  if (url.includes("notifications-hub") || url.includes("hubs/message")) {
    return res;
  }

  // Another request may have refreshed while this request was in flight.
  // Reuse its token instead of rotating the refresh cookie a second time.
  const latestToken = tokenStore.get();
  if (tokenStore.getRevision() !== requestRevision && !latestToken) {
    throw new RefreshSessionError("Session ended during request");
  }
  let newToken = latestToken && latestToken !== requestToken ? latestToken : null;

  try {
    if (!newToken) newToken = await refreshAccessToken();
  } catch (error) {
    // Only an explicit cookie rejection ends the local session. A network,
    // CORS or server failure is recoverable and must not log the user out.
    if (error?.sessionRejected) {
      tokenStore.clear();
      authDebug("LOGOUT", { reason: "refresh_rejected", status: error.status });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth:session-ended"));
      }
    }
    throw error;
  }

  res = await doFetch(url, opts, newToken);
  authDebug("REQUEST_RETRIED", { path: safePath(url) });
  if (!res.ok) {
    // Refresh succeeded, so an endpoint-specific 401/403 must not destroy the
    // newly issued access token.
    throw await toError(res);
  }
  return parseByOptions(res, opts);
}

export const fetchWithAuth = (endpoint, options = {}) => {
  const url = /^https?:\/\//i.test(endpoint) ? endpoint : getApiUrl(endpoint);
  return apiFetch(url, options);
};

async function doFetch(url, opts, token) {
  const method = (opts.method || "GET").toUpperCase();
  const isFormData = opts.body instanceof FormData;
  const baseHeaders = {};

  if (!isFormData && method !== "GET") {
    baseHeaders["Content-Type"] = "application/json";
  }

  const headers = new Headers({
    ...withApiLanguageHeader({ ...baseHeaders, ...(opts.headers || {}) }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  return fetch(url, {
    ...opts,
    headers,
    credentials: "include",
  });
}

async function parseByOptions(res, opts) {
  if (res.status === 204 || res.status === 205) return {};
  if (opts.expectBlob) return res.blob();

  const text = await res.text();
  if (!text) return {};
  try {
    const parseStarted = typeof performance !== "undefined" ? performance.now() : 0;
    const value = JSON.parse(text);
    if (opts.performanceLabel && parseStarted) {
      performance.measure(opts.performanceLabel, {
        start: parseStarted,
        end: performance.now(),
        detail: { responseBytes: new Blob([text]).size },
      });
    }
    return value;
  } catch {
    return text;
  }
}

async function safeParse(res) {
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}

async function toError(res) {
  return createApiError(res);
}

function safePath(url) {
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    return new URL(url, base).pathname;
  } catch {
    return "unknown";
  }
}

function isJwtExpired(token) {
  if (!token) return false;
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === "number" && exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}
